import { useTranslation } from "react-i18next";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const ArrowButton = ({ onClick, disabled, children, label }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
      disabled
        ? "bg-white/[0.02] border-white/5 text-white/30 cursor-not-allowed"
        : "bg-white/5 hover:bg-white/15 border-white/10 text-white"
    }`}
  >
    {children}
  </button>
);

const ShelfHeader = ({
  title,
  icon,
  onSeeAll,
  canScrollLeft,
  canScrollRight,
  onScroll,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const hasOverflow = canScrollLeft || canScrollRight;

  // Each button is tied to the VISUAL direction it points.
  // The "<" button scrolls the shelf visually left (disabled when
  // !canScrollLeft, no matter the language); ">" does the opposite.
  // The parent's onScroll takes a logical "prev"/"next" (back/forward),
  // so in RTL "<" means forward ("next") and ">" means back ("prev").
  const onLeftClick = () => onScroll(isRTL ? "next" : "prev");
  const onRightClick = () => onScroll(isRTL ? "prev" : "next");

  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl lg:text-2xl font-bold text-white">{title}</h2>
      </div>
      <div className="flex items-center gap-2">
        {onSeeAll && (
          <button
            onClick={onSeeAll}
            className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
          >
            {t("general.see_all") || "See all"} →
          </button>
        )}
        {hasOverflow && (
          // dir="ltr" so "<" is always the left button and ">" always the
          // right button, independent of the page's body dir. Without this,
          // flex auto-reverses in RTL and the disabled/click pairing flips
          // against what the user sees.
          <div dir="ltr" className="hidden md:flex items-center gap-1">
            <ArrowButton
              onClick={onLeftClick}
              disabled={!canScrollLeft}
              label={isRTL ? "Next" : "Previous"}
            >
              <CaretLeft size={14} />
            </ArrowButton>
            <ArrowButton
              onClick={onRightClick}
              disabled={!canScrollRight}
              label={isRTL ? "Previous" : "Next"}
            >
              <CaretRight size={14} />
            </ArrowButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShelfHeader;
